import React from "react";
import { Box, Stack } from "@mui/joy";
import { theme } from "styles/theme";
import type { TodoItem } from "./types";

// agent 의 할 일 목록 (write_todos → todo_update 이벤트). 매 갱신마다 전체 목록이
// 와서 통째로 치환된다. status 로 진행/완료를 표시.
export function TodoChecklist({
  todos,
  active = true,
  failed = false,
}: {
  todos: TodoItem[];
  active?: boolean;
  failed?: boolean;
}) {
  if (!todos.length) return null;
  return (
    <Box
      sx={{
        borderRadius: "8px",
        border: `1px solid ${theme.vars.palette.Border.Weak}`,
        backgroundColor: theme.vars.palette.Background.Level2,
        p: "8px 10px",
        mt: "2px",
      }}
    >
      <Box
        sx={{
          fontSize: "10px",
          fontWeight: 700,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: theme.vars.palette.Content.Weak,
          mb: "6px",
        }}
      >
        할 일
      </Box>
      <Stack spacing={0.5}>
        {todos.map((todo, i) => (
          <TodoRow key={i} todo={todo} active={active} failed={failed} />
        ))}
      </Stack>
    </Box>
  );
}

function TodoRow({
  todo,
  active,
  failed,
}: {
  todo: TodoItem;
  active: boolean;
  failed: boolean;
}) {
  const done = todo.status === "completed";
  const inProgress = todo.status === "in_progress";
  const failedTodo = inProgress && failed;
  const spinning = inProgress && active && !failed;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "baseline",
        gap: "8px",
        fontSize: "12px",
        lineHeight: 1.5,
      }}
    >
      <Box
        component="span"
        sx={{
          flexShrink: 0,
          width: "12px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          alignSelf: "center",
        }}
      >
        {done ? (
          <Box
            component="span"
            sx={{ color: "#15803d", fontWeight: 700, fontSize: "12px" }}
          >
            ✓
          </Box>
        ) : failedTodo ? (
          <Box
            component="span"
            sx={{
              color: "#b91c1c",
              fontSize: "12px",
              lineHeight: 1,
              fontWeight: 700,
            }}
          >
            ✕
          </Box>
        ) : spinning ? (
          <Box
            sx={{
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              border: `1.5px solid ${theme.vars.palette.Background.Level3 ?? "#ddd"}`,
              borderTopColor: theme.vars.palette.primary.solid,
              animation: "todoSpin 0.8s linear infinite",
              "@keyframes todoSpin": { to: { transform: "rotate(360deg)" } },
            }}
          />
        ) : (
          <Box component="span" sx={{ color: theme.vars.palette.Content.Weak }}>
            ○
          </Box>
        )}
      </Box>
      <Box
        component="span"
        sx={{
          flex: 1,
          minWidth: 0,
          color: done
            ? theme.vars.palette.Content.Weak
            : spinning || failedTodo
              ? theme.vars.palette.Content.Strong
              : theme.vars.palette.Content.Medium,
          fontWeight: spinning || failedTodo ? 600 : 400,
          textDecoration: done ? "line-through" : "none",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {todo.content}
      </Box>
    </Box>
  );
}
